import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  loadApplication,
  saveApplication,
  submitApplicationForm,
  loadDraftAccess,
  clearDraftAccess,
} from "services/admissions";

/**
 * The one open application, shared by every portal screen.
 *
 * Each screen loads it fresh rather than passing state through the router, so a
 * reloaded page, a bookmarked section and a link opened on a second device all
 * behave the same. The resume token on this device is the only credential; if
 * it is missing or the server rejects it, the family is sent to Continue rather
 * than shown an empty form.
 *
 * `save` reports honestly: the state goes to "failed" and the error is
 * returned, never silently swallowed — a parent must never be told their work
 * is saved when it is not.
 */
export function usePortalApplication() {
  const navigate = useNavigate();
  const access = useRef(loadDraftAccess()).current;

  const [app, setApp] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!access?.applicationNo || !access?.resumeToken) {
      navigate("/admissions-portal/continue", { replace: true });
      return;
    }
    let live = true;
    loadApplication(access.applicationNo, access.resumeToken)
      .then((value) => {
        if (!live) return;
        setApp(value);
        setAnswers(value.answers || {});
        setLoading(false);
      })
      .catch((err) => {
        if (!live) return;
        // A token that no longer opens anything is worse than useless — it
        // would keep bouncing them back here, so drop it.
        if (err.status === 404) {
          clearDraftAccess();
          navigate("/admissions-portal/continue", { replace: true });
          return;
        }
        setLoadError(err.message);
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [access, navigate]);

  /** Edit one field. Marks the draft dirty so the save state stops saying "Saved". */
  const update = useCallback((next) => {
    setAnswers(next);
    setSaveState("unsaved");
  }, []);

  /**
   * Persist. Resolves with null on success and the error on failure, so callers
   * can decide whether to navigate — they must not navigate on a failed save.
   */
  const save = useCallback(
    async (override) => {
      const payload = override || answers;
      setSaveState("saving");
      try {
        const res = await saveApplication(access.applicationNo, access.resumeToken, payload);
        setSaveState("saved");
        setSavedAt(res?.savedAt ? new Date(res.savedAt) : new Date());
        return null;
      } catch (err) {
        setSaveState("failed");
        return err;
      }
    },
    [access, answers]
  );

  /** Save first, then submit — the server validates the stored blob, not this one. */
  const submit = useCallback(
    async (override) => {
      const saveError = await save(override);
      if (saveError) return { error: saveError };
      try {
        const res = await submitApplicationForm(access.applicationNo, access.resumeToken);
        setApp((a) => (a ? { ...a, status: res.status } : a));
        return { result: res };
      } catch (err) {
        return { error: err };
      }
    },
    [access, save]
  );

  return {
    refresh: async () => { const value = await loadApplication(access.applicationNo, access.resumeToken); setApp(value); return value; },
    access,
    app,
    answers,
    update,
    save,
    submit,
    loading,
    loadError,
    saveState,
    savedAt,
    // Families may edit a draft, and a correction the school has asked for.
    editable: !!app && ["DRAFT", "NEEDS_CORRECTION"].includes(app.status),
  };
}
